/**
 * This module provides a number of functions that can help control callbacks so that
 * "dangling" web services don't cause problems.
 *
 * Two issues this module helps solve:
 * 1) Two operations(for example, view a document), have overlapping network requests.
 *      The first "operation" needs to avoid modifying the view model after the second operation has started.
 * 2) A callback is run after a panel has been closed.
 *
 * Definitions:
 * "operation": An operation is the the discrete chunk of work that is done between calls to startNewOp().
 *      Examples include Viewing a Document or Executing a Job View. Note that an operation may contain more than one network request.
 * "component": The overarching UI element that this mixin is attached to.
 * "panel": Used as synonym for component
 *
 * Design for dangling web services:
 * If on panel close, there is still web service callbacks that have not been executed, follow these rules:
 * DO send any notifications that will display outside the panel.
 * DO NOT proceed with anymore network requests.
 * DO NOT send messages to other panels
 * DO NOT modify the current panel.
 *
 * Implementation:
 * Every callback in all panel view models needs to be wrapped in one of the following checks:
 * ifComponent: This callback will only execute if the component is still open
 * ifOp: This callback will only execute if the current operation has not changed and the panel is still open.
 * do: This callback will always execute
 * cleanup: This callback will execute either when it is normally called or when the operation is changed, which ever comes first.
 *
 * Examples:
 *
 * this.startNewOp();
 * this.set("loading", true);
 * ops.makeNetworkRequest()
 *      .then(this.ifOp(function() {
 *          return ops.makeAnotherNetworkRequest()
 *      })
 *      .then(this.ifOp(function() {
 *          this.sendMessage(Panel.VIEWER)
 *      })
 *      .then(this.do(function() {
 *          this.sendNotification();
 *      })
 *      .handle("error/message/PARTIAL_SUCCESS", this.do(function() {
 *          topic.publish()
 *      })
 *      .always(this.cleanup(function() {
 *          this.set("loading", false);
 *      });
 *
 */

function wrapOperationCheck(context, check, f) {
    if (f !== undefined) {
        return (function(data) {
            if (check.call(context)) {
                return f.apply(context, arguments);
            } else {
                return data;
            }
        });
    } else {
        return check.call(context);
    }
}

class Operation {

    constructor(isOpen) {
        this._isOpen = isOpen;
        this._reset();
    }

    _reset() {
        this._toCleanup = [];
        this._guid = kendo.guid();
    }

    start() {
        this._toCleanup.forEach(function(item) {
            item.call(arguments);
        }, this);
        this._reset();
    }

    ifOp(context, f) {
        const guid = this._guid;
        return wrapOperationCheck(context,() => this._isOpen() && this._guid === guid, f);
    }

    cleanup(context, f) {
        const call = this.ifOp(context, f);
        this._toCleanup.push(call);
        const cleanupArray = this._toCleanup;
        return () => {
            const index = cleanupArray.indexOf(call);
            cleanupArray.splice(index, 1);
            return call.apply(context, arguments);
        }
    }
}

export default {

    _isThisComponentStillOpen: true,
    _createdOperations: null,

    _getOpForMixin(op) {
        //Automatically initialize to handle cases where ifOp() is called before initializeFirstOp or after close
        //That is a terrible practice, but seems to be common.
        this.initializeFirstOp();
        if(!this._createdOperations.has(op)) {
            this._createdOperations.set(op, new Operation(() => this._isThisComponentStillOpen));
        }
        return this._createdOperations.get(op);
    },

    /**
     * Mark a panel as being closed.  This is called automatically by MainPanelView.
     */
    markThisComponentAsClosed() {
        this._isThisComponentStillOpen = false;
        this._createdOperations = null;
    },


    /**
     * Initialize operation state. Called by MainPanelViewModel.init() automatically.
     */
    initializeFirstOp: function() {
        if(this._createdOperations === null) {
            this._createdOperations = new Map();
        }
    },

    /**
     * Start a new operation.  This should be called when a new operation is needed.
     * @param op Operation name. Optional
     */
    startNewOp(op = "default") {
        this._getOpForMixin(op).start();
    },

    /**
     * Wrap a function so that is is registered as a "cleanup" function.
     *
     * The function will be called at most once.  It will be called when the current operation is stopped.
     *
     * It may also be called when the returned function is called directly.
     *
     * This is most useful for .always handlers.
     * @param op The operation name. Optional.
     * @param f Target cleanup function
     * @returns {*}
     */
    cleanup(op, f) {
        if(typeof op === "function") {
            return this.cleanup("default", op);
        } else {
            return this._getOpForMixin(op).cleanup(this, f);
        }
    },

    /**
     * Wrap a function in a check that always passes.
     * This is functionally useless, but it is a useful marker for someone else that you intentionally have no checks around a callback.
     * @param f
     * @returns {*}
     */
    do(f) {
        return wrapOperationCheck(this, () => true, f);
    },

    /**
     * Wrap a function so that it will only be executed if the component is still open.
     * @param f
     * @returns {*}
     */
    ifComponent(f) {
        return wrapOperationCheck(this, () => this._isThisComponentStillOpen, f);
    },

    /**
     * Identical to ifComponent, exists to avoid refactoring.
     * @param f
     * @returns {*}
     */
    ifPanel(f) {
        return this.ifComponent(f);
    },

    /**
     * Wrap a function so that it will only be executed if the current operation is still in progress.
     * @param op Operation name. Optional.
     * @param f Function to wrap.
     * @returns {*}
     */
    ifOp(op = "default", f) {
        if(typeof op === "function") {
            return this.ifOp("default", op);
        } else {
            return this._getOpForMixin(op).ifOp(this, f);
        }
    }
};
